// ElementBlock — the shared shell around one element, pure.
//
// This is the O(1) shared shell of the editor (project_editor_is_material_
// surface): it is template-AGNOSTIC and renders no element kind itself. It
// receives the per-kind `body` (rendered by the registry) and wraps it in the
// wear every element carries regardless of kind:
//   • a GUTTER of ChangeMarkers — the proposal marker (derived from `proposal`)
//     plus any issue / grounding markers the caller composes;
//   • a ProvenanceTag — who last authored this, and whether anything is pending;
//   • the REVIEW surface — when open, the op-aware ReviewControl anchored below
//     the element (.claude/research/design/03: approve AT the change).
//
// The field-vs-block seam (the pluggable-renderer boundary): a FIELD-grain op
// (scalar-edit / list-edit) is content-internal, so the per-kind renderer draws
// its InlineDiff inside `body`; a BLOCK-grain op (add / remove / move) is
// structural, so THIS shell wraps `body` in the block-level InlineDiff. Each
// side decides by matching the op — no flag is threaded. Structural change is
// the shell's job; content change is the renderer's, exactly the own-the-AST
// split.
//
// Pure/props-driven: content + proposal + markers in; every interaction reports
// UP (toggle review, pick an outcome, activate a marker). No edit state here —
// the parent (MaterialDocument, then the app) owns it.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../../Icon/Icon.js";
import { IconButton } from "../../IconButton/IconButton.js";
import { ChangeMarker } from "../ChangeMarker/ChangeMarker.js";
import { InlineDiff } from "../InlineDiff/InlineDiff.js";
import { ProvenanceTag } from "../ProvenanceTag/ProvenanceTag.js";
import { ReviewControl } from "../ReviewControl/ReviewControl.js";
import type {
    DiffOp,
    GutterMarker,
    PendingProposal,
    Provenance,
    ReviewOutcome,
} from "../types.js";
import "./ElementBlock.css";

export type ElementBlockProps = {
    // The per-kind rendered content (from the renderer registry).
    readonly body: ReactNode;
    readonly authoredBy: Provenance;
    // The pending agent proposal on this element, if any.
    readonly proposal?: PendingProposal;
    // Extra gutter wear beyond the proposal marker (issues, grounding read-wear).
    readonly markers?: readonly GutterMarker[];
    // Whether the review surface is open (controlled by the parent).
    readonly reviewOpen?: boolean;
    readonly onToggleReview?: () => void;
    readonly onReview?: (outcome: ReviewOutcome) => void;
    readonly onMarkerActivate?: (marker: GutterMarker) => void;
    readonly onJumpToSource?: (anchorIndex: number) => void;
    readonly overTrustCue?: boolean;
    // Structural block controls — the focus-revealed gutter grip. Each is present
    // only when the document is editable AND the move is legal at this position:
    // `onMoveUp` is absent on the first block, `onMoveDown` on the last, so the
    // affordance never offers an impossible move. `onRemove` deletes the block.
    // Keyboard-operable buttons (not hover-only drag): the wedge-floor a11y bar.
    readonly onMoveUp?: () => void;
    readonly onMoveDown?: () => void;
    readonly onRemove?: () => void;
};

// A block-grain op is wrapped by the shell; a field-grain op is drawn by the
// renderer inside the body. Named so the seam is legible at the call site.
const isBlockOp = (op: DiffOp): boolean =>
    op.type === "add" || op.type === "remove" || op.type === "move";

export const ElementBlock = ({
    body,
    authoredBy,
    proposal,
    markers = [],
    reviewOpen,
    onToggleReview,
    onReview,
    onMarkerActivate,
    onJumpToSource,
    overTrustCue,
    onMoveUp,
    onMoveDown,
    onRemove,
}: ElementBlockProps) => {
    const { t } = useLingui();
    // Block-grain proposals wrap the body in the shell's InlineDiff; field-grain
    // proposals were already drawn inside `body` by the renderer.
    const content =
        proposal && isBlockOp(proposal.op) ? (
            <InlineDiff op={proposal.op}>{body}</InlineDiff>
        ) : (
            body
        );

    // The structural controls exist only when the document is editable (at least
    // one handler present). Focus-revealed via CSS (:focus-within / hover), so a
    // reading face stays clean but the grip is always keyboard-reachable.
    const hasControls =
        onMoveUp !== undefined ||
        onMoveDown !== undefined ||
        onRemove !== undefined;

    return (
        <div
            className={`bilda-element-block${
                proposal ? " bilda-element-block--pending" : ""
            }`}
        >
            <aside className="bilda-element-block__gutter">
                {proposal ? (
                    <ChangeMarker
                        marker={{ type: "proposal" }}
                        expanded={reviewOpen}
                        onActivate={onToggleReview}
                    />
                ) : null}
                {markers.map((marker, i) => (
                    <ChangeMarker
                        // biome-ignore lint/suspicious/noArrayIndexKey: sketch fixture — markers are a small, static, non-reorderable per-element list; real markers will carry stable ids
                        key={`${marker.type}-${i}`}
                        marker={marker}
                        onActivate={() => onMarkerActivate?.(marker)}
                    />
                ))}
                {hasControls ? (
                    <div className="bilda-element-block__controls">
                        {onMoveUp !== undefined ? (
                            <IconButton
                                variant="ghost"
                                size="sm"
                                label={t`Move block up`}
                                onClick={onMoveUp}
                            >
                                <Icon name="chevron-up" size="sm" />
                            </IconButton>
                        ) : null}
                        {onMoveDown !== undefined ? (
                            <IconButton
                                variant="ghost"
                                size="sm"
                                label={t`Move block down`}
                                onClick={onMoveDown}
                            >
                                <Icon name="chevron-down" size="sm" />
                            </IconButton>
                        ) : null}
                        {onRemove !== undefined ? (
                            <IconButton
                                variant="ghost"
                                size="sm"
                                label={t`Remove block`}
                                onClick={onRemove}
                            >
                                <Icon name="trash" size="sm" />
                            </IconButton>
                        ) : null}
                    </div>
                ) : null}
            </aside>

            <div className="bilda-element-block__main">
                <div className="bilda-element-block__wear">
                    <ProvenanceTag authoredBy={authoredBy} pending={proposal} />
                </div>
                <div
                    className="bilda-element-block__body"
                    // Names this div as the block's CONTENT ONLY — excludes
                    // the gutter, ProvenanceTag and review surface, which
                    // also live inside the outer `[data-element-id]` block
                    // wrapper. MaterialDocument's copy/cut handler reads
                    // this node's `textContent` to test whether a
                    // single-block selection covers the block's ENTIRE
                    // text (as opposed to a partial in-block range) — the
                    // ONLY reason this attribute exists.
                    data-block-body=""
                >
                    {content}
                </div>
                {proposal && reviewOpen ? (
                    <div className="bilda-element-block__review">
                        <ReviewControl
                            proposal={proposal}
                            onReview={(o) => onReview?.(o)}
                            overTrustCue={overTrustCue}
                            onJumpToSource={onJumpToSource}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
};
